import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";
import { z } from "zod";

import { faCirclePlus, faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, InputText, Table } from "@performyard/py-components";
import { ReviewCycleReportRowFrontend } from "@performyard/service-contracts";

import Spacer from "../../../../components/Spacer";

import { useReviewCycleReportsStream } from "../../../../api/hooks";
import { useRbacObjectPermissions } from "../../../../api/hooks/defs/rbac";
import useSearchQueryParams from "../../../../hooks/useSearchQueryParams";

import { getReviewFormsColumns } from "./columns";

const columns = getReviewFormsColumns();

// Kept in the URL (like the Review Subjects search) so it survives switching tabs
const FormsSearchSchema = z.object({
	formSearch: z.string().default(""),
});

const ReviewFormsTable = ({ reviewCycleId }: { reviewCycleId: string }) => {
	const navigate = useNavigate();
	const { data: rows, isFetching, isError } = useReviewCycleReportsStream(reviewCycleId);
	const { data: permissions } = useRbacObjectPermissions("review", reviewCycleId);
	const canAddForms = permissions?.includes("review_cycle:addFormsInCycle") ?? false;

	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: FormsSearchSchema });
	const search = queryParams.formSearch;
	const [debouncedSearch] = useDebounceValue(search, 200);

	// Sorted here rather than with a column sort so the default order shows no sort indicators
	const filteredRows = useMemo(() => {
		const term = debouncedSearch.trim().toLowerCase();
		const matching = term
			? (rows ?? []).filter((row) => row.form.name.toLowerCase().includes(term))
			: [...(rows ?? [])];

		return matching.sort(
			(a, b) => a.form.name.localeCompare(b.form.name) || a.subject.name.localeCompare(b.subject.name)
		);
	}, [rows, debouncedSearch]);

	return (
		<div className="flex-col gap-2">
			<div className="flex w-full items-center justify-between gap-2">
				<div className="w-full max-w-[278px] min-w-[278px]">
					<InputText
						id="review-forms-search"
						placeholder="Search forms..."
						aria-label="Search forms"
						leftIcon={faMagnifyingGlass}
						value={search}
						onChange={(value) => handleQueryChange({ formSearch: value })}
						clearable={search !== ""}
					/>
				</div>
				{canAddForms && (
					<ButtonStandard
						color="neutral"
						variant="outline"
						size="medium"
						iconLeft={faCirclePlus}
						onClick={() => navigate(`/review-cycle/${reviewCycleId}/add-forms/`)}
						dangerouslySetClassName="w-auto"
						data-testid="review-forms-add-forms"
					>
						Add Forms
					</ButtonStandard>
				)}
			</div>
			<Spacer height={2} />
			<div className="h-[700px] w-full">
				<Table<ReviewCycleReportRowFrontend>
					columnSizing="fit-grid"
					rowData={filteredRows}
					columnDefs={columns}
					getRowId={({ data }) => data._id}
					rowHeight={40}
					loading={isFetching && (rows?.length ?? 0) === 0}
					emptyStateText={
						isError ? "We couldn't load the review forms. Please refresh to try again." : "No results found"
					}
					suppressMultiSort={true} // Only ever show one sort indicator
					suppressScrollOnNewData={true}
					suppressCellFocus={true}
					suppressRowHoverHighlight={true}
				/>
			</div>
		</div>
	);
};

export default ReviewFormsTable;
